import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { X } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";

function displayValue(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const date = new Date(String(iso).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${dd} ${months[date.getMonth()]} ${date.getFullYear()} ${hh}:${mm}`;
}

function formatDateOnly(iso) {
  if (!iso) return "—";
  const raw = String(iso).slice(0, 10);
  const [y, m, d] = raw.split("-");
  if (!y || !m || !d) return formatDateTime(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

function formatAmount(amount, currency) {
  if (amount == null || amount === "") return "—";
  const num = Number(amount);
  const formatted = Number.isFinite(num) ?
  num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) :
  String(amount);
  return currency ? `${currency} ${formatted}` : formatted;
}

function formatPct(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

function DetailField({ label, children, className }) {
  return (
    <div className={cn("space-y-0.5", className)}>
      <span className="block text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
        {label}
      </span>
      <div className="text-sm text-foreground break-words">{children}</div>
    </div>);

}

function SectionTitle({ children }) {
  return (
    <span className="block text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)] mb-2">
      {children}
    </span>);

}

export function QuotationDetailsModal({ open, onOpenChange, quotation }) {
  if (!quotation) return null;

  const t = i18n.t.bind(i18n);
  const currency = quotation.currency || "";
  const lineItems = Array.isArray(quotation.lineItems) ? quotation.lineItems : [];
  const systemSummaries = Array.isArray(quotation.systemSummaries) ? quotation.systemSummaries : [];
  const pmcs = Array.isArray(quotation.pmcs) ? quotation.pmcs : [];
  const specificTerms = Array.isArray(quotation.specificTerms) ? quotation.specificTerms : [];
  const pmcsWithCost = pmcs.filter((row) => Number(row.totalCost) > 0);
  const sellingTotal =
    quotation.finalSellingPrice ??
    quotation.totalAmount ??
    quotation.totalSellingSupplyAmount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[92vw] md:w-[760px] max-w-[760px]",
            "max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "font-sans"
          )}>
          
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--color-text-border)]/70 bg-white px-4 py-3 shrink-0">
            <DialogTitle className="text-[18px] font-semibold text-foreground tracking-tight">
              <Trans i18nKey="fsm.quotationDetails" />
            </DialogTitle>
            <DialogPrimitive.Close
              className="h-8 w-8 grid place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition">
              
              <X className="h-4 w-4" />
              <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-4">
            <div>
              <SectionTitle><Trans i18nKey="fsm.quotation" /></SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailField label={t("fsm.quotationNumber")}>
                  {displayValue(quotation.quotationNumber || quotation.id)}
                </DetailField>
                <DetailField label={t("status.status")}>
                  {displayValue(quotation.status)}
                </DetailField>
                {quotation.type ?
                <DetailField label={t("salesQuotation.quotationType")}>
                    {displayValue(quotation.type)}
                  </DetailField> :
                null}
                {quotation.versionNo != null && quotation.versionNo !== "" ?
                <DetailField label={t("salesQuotation.version")}>
                    {displayValue(quotation.versionNo)}
                  </DetailField> :
                null}
                <DetailField label={t("salesQuotation.sellingPrice")}>
                  {formatAmount(sellingTotal, currency)}
                </DetailField>
                <DetailField label={t("fsm.validUntil")}>
                  {formatDateOnly(quotation.validUntil)}
                </DetailField>
                <DetailField label={t("dateTime.createdOn")}>
                  {formatDateTime(quotation.createdOn)}
                </DetailField>
                {quotation.updatedOn ?
                <DetailField label={t("salesQuotation.lastUpdated")}>
                    {formatDateTime(quotation.updatedOn)}
                  </DetailField> :
                null}
                {quotation.createdByName ?
                <DetailField label={t("common.createdBy")}>
                    {displayValue(quotation.createdByName)}
                  </DetailField> :
                null}
              </div>
            </div>

            <div>
              <SectionTitle><Trans i18nKey="common.customer" /></SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailField label={t("common.customer")}>
                  {displayValue(quotation.customerName)}
                </DetailField>
                <DetailField label={t("common.location4")}>
                  {displayValue(quotation.locationName)}
                </DetailField>
                <DetailField label={t("common.contact3")}>
                  {displayValue(quotation.contactName)}
                </DetailField>
                <DetailField label={t("common.phone")}>
                  {displayValue(quotation.contactPhone)}
                </DetailField>
                <DetailField label={t("common.email")} className="sm:col-span-2">
                  {displayValue(quotation.contactEmail)}
                </DetailField>
              </div>
            </div>

            <div>
              <SectionTitle><Trans i18nKey="inventory.total" /></SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailField label={t("fsm.totalInternalCost")}>
                  {formatAmount(quotation.totalInternalCost, currency)}
                </DetailField>
                <DetailField label={t("fsm.totalSellingSupply")}>
                  {formatAmount(quotation.totalSellingSupplyAmount, currency)}
                </DetailField>
                <DetailField label={t("fsm.totalSellingInstallation")}>
                  {formatAmount(quotation.totalSellingInstallationAmount, currency)}
                </DetailField>
                <DetailField label={t("salesQuotation.sellingPrice")}>
                  {formatAmount(sellingTotal, currency)}
                </DetailField>
                <DetailField label={t("fsm.grossProfit")}>
                  {formatAmount(quotation.grossProfit, currency)}
                </DetailField>
                <DetailField label={t("fsm.grossMargin")}>
                  {formatPct(quotation.grossMarginPct)}
                </DetailField>
                <DetailField label={t("fsm.netProfit")}>
                  {formatAmount(quotation.netProfit, currency)}
                </DetailField>
                <DetailField label={t("fsm.netMargin")}>
                  {formatPct(quotation.netMarginPct)}
                </DetailField>
                {quotation.overheadsPct != null ?
                <DetailField label={t("fsm.overheadsPct")}>
                    {formatPct(quotation.overheadsPct)}
                  </DetailField> :
                null}
                {quotation.markUpPct != null ?
                <DetailField label={t("fsm.markUpPct")}>
                    {formatPct(quotation.markUpPct)}
                  </DetailField> :
                null}
              </div>
            </div>

            {(quotation.managerNote || quotation.remarks) ?
            <DetailField label={quotation.managerNote ? t("common.notes") : t("common.notes")}>
                {displayValue(quotation.managerNote || quotation.remarks)}
              </DetailField> :
            null}

            {lineItems.length > 0 ?
            <div className="space-y-2">
                <SectionTitle><Trans i18nKey="fsm.quotationItems" /></SectionTitle>
                <div className="overflow-x-auto rounded-xl border border-[var(--color-text-border)]/50">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead>
                      <tr className="bg-[var(--color-workspace)]/50 text-left text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">
                        <th className="px-3 py-2 font-semibold">#</th>
                        <th className="px-3 py-2 font-semibold"><Trans i18nKey="common.description" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.brand" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.system" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="inventory.qty" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="inventory.uom" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.sellingRate" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="inventory.total" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((row, index) =>
                      <tr
                        key={row.id ?? index}
                        className="border-t border-[var(--color-text-border)]/40 align-top">
                        
                          <td className="px-3 py-2 whitespace-nowrap">{displayValue(row.slNo ?? index + 1)}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-foreground">{displayValue(row.description)}</div>
                            {row.hsnCode ?
                          <div className="text-[11px] text-[var(--color-text-secondary)] mt-0.5">
                                HSN: {displayValue(row.hsnCode)}
                              </div> :
                          null}
                            {row.remarks ?
                          <div className="text-[11px] text-[var(--color-text-secondary)] mt-0.5">
                                {displayValue(row.remarks)}
                              </div> :
                          null}
                          </td>
                          <td className="px-3 py-2">{displayValue(row.brandId)}</td>
                          <td className="px-3 py-2">{displayValue(row.systemName)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{displayValue(row.quantity)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{displayValue(row.uom)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatAmount(row.sellingSupplyRate ?? row.unitPrice, currency)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap font-medium">
                            {formatAmount(row.sellingSupplyAmount ?? row.amount, currency)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div> :
            null}

            {systemSummaries.length > 0 ?
            <div className="space-y-2">
                <SectionTitle><Trans i18nKey="fsm.systemSummary" /></SectionTitle>
                <div className="overflow-x-auto rounded-xl border border-[var(--color-text-border)]/50">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="bg-[var(--color-workspace)]/50 text-left text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">
                        <th className="px-3 py-2 font-semibold"><Trans i18nKey="fsm.system" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.totalCost" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="salesQuotation.sellingPrice" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.grossProfit" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.grossMargin" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemSummaries.map((row, index) =>
                      <tr
                        key={`${row.systemName}-${index}`}
                        className="border-t border-[var(--color-text-border)]/40">
                        
                          <td className="px-3 py-2">{displayValue(row.systemName)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatAmount(row.totalCostPrice, currency)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatAmount(row.totalSellingPrice, currency)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatAmount(row.grossProfit, currency)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatPct(row.grossMarginPct)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div> :
            null}

            {pmcsWithCost.length > 0 ?
            <div className="space-y-2">
                <SectionTitle><Trans i18nKey="salesQuotation.pmcCostHeadsTitle" /></SectionTitle>
                <div className="overflow-x-auto rounded-xl border border-[var(--color-text-border)]/50">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[var(--color-workspace)]/50 text-left text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">
                        <th className="px-3 py-2 font-semibold"><Trans i18nKey="common.category" /></th>
                        <th className="px-3 py-2 font-semibold"><Trans i18nKey="common.name" /></th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap"><Trans i18nKey="inventory.total" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pmcsWithCost.map((row, index) =>
                      <tr
                        key={row.id ?? index}
                        className="border-t border-[var(--color-text-border)]/40">
                        
                          <td className="px-3 py-2">{displayValue(row.category)}</td>
                          <td className="px-3 py-2">{displayValue(row.headName)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatAmount(row.totalCost, currency)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div> :
            null}

            {specificTerms.length > 0 ?
            <div className="space-y-2">
                <SectionTitle><Trans i18nKey="fsm.termsAndConditions" /></SectionTitle>
                <ul className="space-y-2">
                  {specificTerms.map((term, index) =>
                  <li
                    key={term.id ?? index}
                    className="rounded-xl border border-[var(--color-text-border)]/50 px-3 py-2.5">
                    
                      <p className="text-sm font-semibold text-foreground">
                        {displayValue(term.title || `Term ${index + 1}`)}
                      </p>
                      {term.description ?
                    <p className="text-sm text-[var(--color-text-secondary)] mt-1 whitespace-pre-wrap">
                          {displayValue(term.description)}
                        </p> :
                    null}
                    </li>
                  )}
                </ul>
              </div> :
            null}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}
